import GooglePlacesInput from "@/components/place/google-places-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandColors, Colors } from "@/constants/theme";
import { useDesktopSheetStyle } from "@/hooks/use-desktop-layout";
import { AppRole } from "@/lib/roles";
import { BottomSheetModal, BottomSheetView } from "@gorhom/bottom-sheet";
import Ionicons from "@react-native-vector-icons/ionicons";
import React, { useMemo, useRef, useState } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  Path,
  UseFormSetValue,
} from "react-hook-form";
import {
  StyleProp,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
  useColorScheme,
} from "react-native";
import { GooglePlacesAutocompleteRef } from "react-native-google-places-autocomplete";
import { HelperText } from "react-native-paper";
import { ThemedText } from "./themed-text";

/**
 * The subset of fields that are shared between RegisterFormData and UserFormData.
 * Both schemas must include these keys for UserFormFields to work correctly.
 */
export interface SharedUserFields {
  first_name: string;
  middle_name?: string;
  last_name: string;
  email: string;
  phone: string;
  address?: string;
  origin_city?: string;
  origin_latitude?: number;
  origin_longitude?: number;
  role: AppRole;
}

interface UserFormFieldsProps<T extends SharedUserFields> {
  control: Control<T>;
  errors: FieldErrors<T>;
  setValue: UseFormSetValue<T>;
  /**
   * The currently selected role — used to show the driver hint for origin city.
   * Pass `watch("role")` from the parent form.
   */
  role?: AppRole;
  /**
   * Which group of fields to render. `name` covers first, middle and last name;
   * `contact` covers email, phone and address.
   */
  section?: "all" | "name" | "contact";
  style?: StyleProp<ViewStyle>;
  inputStyle?: StyleProp<TextStyle>;
  inputOutlineStyle?: StyleProp<ViewStyle>;
}

/**
 * Reusable form fields shared between the Register and Edit Profile screens.
 * Renders: first name, last name, middle name, email, phone, address, origin city.
 *
 * Register-only fields (password, referral code, role selector) are handled
 * by the respective screens themselves.
 */
export function UserFormFields<T extends SharedUserFields>({
  control,
  errors,
  setValue,
  role,
  section = "all",
  style,
  inputStyle,
  inputOutlineStyle,
}: UserFormFieldsProps<T>) {
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const addressRef = useRef<GooglePlacesAutocompleteRef>(null);
  // Two points so index={1} opens at 95% (same pattern as AddressRegistration).
  const routeSheetSnapPoints = useMemo(() => ["90%", "95%"], []);
  const routeSheetRef = useRef<BottomSheetModal>(null);
  const desktopSheetStyle = useDesktopSheetStyle();
  const [place, setPlace] = useState<any>({});

  const showNameFields = section === "all" || section === "name";
  const showContactFields = section === "all" || section === "contact";

  const closeRouteSheet = () => {
    routeSheetRef.current?.dismiss();
  };

  return (
    <View style={[styles.form, style]}>
      {showNameFields && (
        <>
          <View style={styles.nameRow}>
            <View style={[styles.field, styles.nameField]}>
              <Label label="First Name" />
              <Controller
                control={control}
                name={"first_name" as Path<T>}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    placeholder="First name"
                    value={value as string}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    autoComplete="name-given"
                    error={!!errors.first_name}
                    style={[styles.input, inputStyle]}
                    outlineStyle={[styles.inputOutline, inputOutlineStyle]}
                    testID="first-name"
                  />
                )}
              />
              {errors.first_name && (
                <HelperText type="error" visible={!!errors.first_name}>
                  {errors.first_name?.message as string}
                </HelperText>
              )}
            </View>

            <View style={[styles.field, styles.nameField]}>
              <Label label="Middle Name" required={false} />
              <Controller
                control={control}
                name={"middle_name" as Path<T>}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    placeholder="Middle name"
                    value={value as string}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    error={!!errors.middle_name}
                    style={[styles.input, inputStyle]}
                    outlineStyle={[styles.inputOutline, inputOutlineStyle]}
                    testID="middle-name"
                  />
                )}
              />
              {errors.middle_name && (
                <HelperText type="error" visible={!!errors.middle_name}>
                  {errors.middle_name?.message as string}
                </HelperText>
              )}
            </View>
          </View>

          <View style={styles.field}>
            <Label label="Last Name" />
            <Controller
              control={control}
              name={"last_name" as Path<T>}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  placeholder="Last name"
                  value={value as string}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  autoComplete="name-family"
                  error={!!errors.last_name}
                  style={[styles.input, inputStyle]}
                  outlineStyle={[styles.inputOutline, inputOutlineStyle]}
                  testID="last-name"
                />
              )}
            />
            {errors.last_name && (
              <HelperText type="error" visible={!!errors.last_name}>
                {errors.last_name?.message as string}
              </HelperText>
            )}
          </View>
        </>
      )}

      {showContactFields && (
        <>
          <View style={styles.field}>
            <Label label="Email" />
            <Controller
              control={control}
              name={"email" as Path<T>}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  placeholder="Enter your email"
                  value={value as string}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoComplete="email"
                  error={!!errors.email}
                  style={[styles.input, inputStyle]}
                  outlineStyle={[styles.inputOutline, inputOutlineStyle]}
                  testID="email"
                />
              )}
            />
            {errors.email && (
              <HelperText type="error" visible={!!errors.email}>
                {errors.email?.message as string}
              </HelperText>
            )}
          </View>

          <View style={styles.field}>
            <Label label="Phone" />
            <Controller
              control={control}
              name={"phone" as Path<T>}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  placeholder="Enter your phone number"
                  value={value as string}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  keyboardType="phone-pad"
                  autoComplete="tel"
                  error={!!errors.phone}
                  style={[styles.input, inputStyle]}
                  outlineStyle={[styles.inputOutline, inputOutlineStyle]}
                  testID="phone"
                />
              )}
            />
            {errors.phone && (
              <HelperText type="error" visible={!!errors.phone}>
                {errors.phone?.message as string}
              </HelperText>
            )}
          </View>

          <View style={styles.field}>
            <Label label="Address" />
            <Controller
              control={control}
              name={"address" as Path<T>}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  placeholder="Enter your address"
                  value={value as string}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  onFocus={(e) => {
                    e.preventDefault();
                    routeSheetRef.current?.present();
                    setTimeout(() => {
                      addressRef.current?.setAddressText(place.address || "");
                      addressRef.current?.focus();
                    }, 200);
                  }}
                  error={!!errors.address}
                  style={[styles.input, inputStyle]}
                  outlineStyle={[styles.inputOutline, inputOutlineStyle]}
                  testID="address"
                />
              )}
            />
            {errors.address && (
              <HelperText type="error" visible={!!errors.address}>
                {errors.address?.message as string}
              </HelperText>
            )}
          </View>

          <BottomSheetModal
            ref={routeSheetRef}
            index={1}
            snapPoints={routeSheetSnapPoints}
            style={desktopSheetStyle}
            keyboardBehavior="extend"
            keyboardBlurBehavior="restore"
            android_keyboardInputMode="adjustResize"
            enableContentPanningGesture={false}
            backgroundStyle={[
              styles.routeSheetBackground,
              { backgroundColor: theme.background },
            ]}
            handleIndicatorStyle={styles.routeSheetHandle}
          >
            <BottomSheetView style={styles.routeSheetContent}>
              <View style={styles.routeSheetHeader}>
                <View>
                  <ThemedText type="subtitle">Enter your address</ThemedText>
                </View>
                <TouchableOpacity
                  onPress={closeRouteSheet}
                  style={styles.routeCloseButton}
                >
                  <Ionicons name="close" size={20} color={theme.text} />
                </TouchableOpacity>
              </View>

              <View style={styles.inputGroup}>
                <GooglePlacesInput
                  ref={addressRef}
                  isPickoff
                  isPredefined={false}
                  placeholder="Search address..."
                  onPlaceSelected={(place) => {
                    setPlace(place);
                    setValue("address" as Path<T>, place.address as any);
                    setValue("origin_city" as Path<T>, place.city as any);
                    setValue(
                      "origin_latitude" as Path<T>,
                      place.latitude as any,
                    );
                    setValue(
                      "origin_longitude" as Path<T>,
                      place.longitude as any,
                    );
                    closeRouteSheet();
                  }}
                />
              </View>
            </BottomSheetView>
          </BottomSheetModal>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  form: {
    gap: 20,
  },
  nameRow: {
    flexDirection: "row",
    gap: 12,
  },
  field: {
    gap: 8,
  },
  nameField: {
    flex: 1,
  },
  input: {
    fontSize: 16,
  },
  inputOutline: {
    borderRadius: 12,
  },
  hint: {
    fontSize: 12,
    color: BrandColors.mediumGray,
    marginTop: -8,
    marginBottom: 8,
  },
  routeSheetBackground: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  routeSheetHandle: {
    backgroundColor: "#475569",
    marginTop: 8,
  },
  routeSheetContent: {
    flex: 1,
    padding: 24,
  },
  routeSheetHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  routeCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
  },
  inputGroup: {
    gap: 8,
  },
  searchCard: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
    borderWidth: 1,
    backgroundColor: "red",
  },
  searchCTAText: {
    fontWeight: "700",
    color: BrandColors.mediumGray,
  },
  inputError: {
    borderColor: BrandColors.error,
    borderWidth: 1,
  },
});
